import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from '@/integrations/supabase/client';
import * as Sentry from "@sentry/react";
import { SlideAnimation } from "@/components/ui/css-animations";
import { Button } from "@/components/ui/button";
import { calculateScores } from "@/services/scoring/calculateScores";
import { syncAdvisorsToTeamMembers } from "@/services/syncAdvisorsToTeamMembers";
import { syncA8SuccessorsToCandidates } from "@/services/syncA8SuccessorsToCandidates";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";

import { QuestionRenderer } from "./conversational/QuestionRenderer";
import { CompositeQuestionGroup } from "./conversational/CompositeQuestionGroup";
import { AdvisorTypeSectionCard, getAdvisorTypeFromKey } from "./conversational/AdvisorTypeSectionCard";
import { useAssessment } from "@/context/AssessmentContext";
import { AssessmentNavigation } from "./conversational/AssessmentNavigation";
import { AnswerData } from "./conversational/types";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAssessmentPersistence } from "@/hooks/useAssessmentPersistence";
import { useAuth } from "@/contexts/AuthContext";
import { AssessmentErrorBoundary } from "./AssessmentErrorBoundary";
import { InlineLoading } from "@/components/ui/loading-states";
import { useQuestionLogic } from "@/hooks/useQuestionLogic";
import { evaluateConditionalDisplay } from "@/hooks/useConditionEvaluation";
import { isSelectAnswer, QuestionAnswer } from "./conversational/answerTypes";
import { logger } from "@/utils/logger";
import { isOtherValue, otherTextKey } from "./conversational/OtherTextInput";
import { useAssessmentQuestions } from "@/hooks/useAssessmentQuestions";
import { getAssessmentIdByModuleId, getAssessmentMetaByModuleId, type AssessmentMeta } from "@/utils/assessmentLookup";
import { AssessmentIntroScreen } from "./conversational/AssessmentIntroScreen";
import { AssessmentPlaceholder } from "./AssessmentPlaceholder";
import { RecallBridgeScreen } from "./conversational/RecallBridgeScreen";
import type { LevelSetQuestion } from "./conversational/questionTypes";

interface ConversationalAssessmentProps {
  onComplete: () => void;
  onExit: () => void;
  onNextAssessment?: () => void;
  moduleId?: string;
}

export function ConversationalAssessment({
  onComplete,
  onExit,
  onNextAssessment,
  moduleId: propModuleId
}: ConversationalAssessmentProps) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswerData>({});
  const [animationDirection, setAnimationDirection] = useState<'right' | 'left'>('right');
  const [isInitializing, setIsInitializing] = useState(true);
  const [isFinishing, setIsFinishing] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [assessmentMeta, setAssessmentMeta] = useState<AssessmentMeta | null>(null);
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentModuleId } = useAssessment();

  const effectiveModuleId = propModuleId || currentModuleId;
  logger.debug('ConversationalAssessment effectiveModuleId', { effectiveModuleId });

  if (!effectiveModuleId) {
    logger.warn('No effective module ID available, using default assessment');
  }

  const [resolvedAssessmentId, setResolvedAssessmentId] = useState<string | null>(null);
  useEffect(() => {
    async function resolveAssessmentId() {
      if (effectiveModuleId) {
        const id = await getAssessmentIdByModuleId(effectiveModuleId);
        setResolvedAssessmentId(id);
        const meta = await getAssessmentMetaByModuleId(effectiveModuleId);
        setAssessmentMeta(meta);
      }
    }
    resolveAssessmentId();
  }, [effectiveModuleId]);

  // Fetch DB questions when we have a resolved assessment ID
  const { questions: dbQuestions, loading: dbLoading, error: dbError } = useAssessmentQuestions(
    resolvedAssessmentId
  );

  // Build a level-set question from DB meta (for all modules)
  const dbLevelSetQuestion: LevelSetQuestion | null = useMemo(() => {
    if (!assessmentMeta?.levelSetContent) return null;
    const lsc = assessmentMeta.levelSetContent;
    return {
      id: `${effectiveModuleId}-level-set`,
      type: 'level-set' as const,
      title: lsc.title,
      description: lsc.description,
      required: false,
      sections: lsc.sections,
      buttonText: lsc.buttonText,
    };
  }, [assessmentMeta, effectiveModuleId]);

  // All modules use DB questions
  const moduleQuestions = useMemo(() => {
    if (dbQuestions.length > 0) {
      const questions = [...dbQuestions];
      // Inject level-set before first question if available
      if (dbLevelSetQuestion) {
        questions.unshift(dbLevelSetQuestion);
      }
      // Inject recall bridge for A2 (owner-readiness-exit-timeline) after level-set
      if (effectiveModuleId === 'owner-readiness-exit-timeline') {
        const insertIndex = dbLevelSetQuestion ? 1 : 0;
        questions.splice(insertIndex, 0, {
          id: `${effectiveModuleId}-recall-bridge`,
          type: 'recall-bridge' as any,
          title: 'Your Exit Timeline',
          required: false,
        });
      }
      // Append completion screen
      questions.push({
        id: `${effectiveModuleId}-completion`,
        type: 'completion' as const,
        title: 'Assessment Complete',
        required: false,
      });
      return questions;
    }
    return dbQuestions;
  }, [dbQuestions, dbLevelSetQuestion, effectiveModuleId]);

  logger.debug('Module questions loaded', {
    count: moduleQuestions.length,
    firstQuestionTitle: moduleQuestions[0]?.title,
  });

  const {
    loadLocalProgress,
    autoSave,
    manualSave,
    saveToDatabase,
    saveAllResponses,
    clearProgress,
    updateAssessmentData,
    isSaving,
    teamId,
    assessmentId
  } = useAssessmentPersistence(
    effectiveModuleId || 'snapshot-assessment',
    resolvedAssessmentId || undefined
  );

  const { getVisibleQuestions } = useQuestionLogic(moduleQuestions, answers);

  // Load saved progress on mount
  useEffect(() => {
    const savedProgress = loadLocalProgress();
    if (savedProgress) {
      setCurrentQuestionIndex(savedProgress.questionIndex);
      setAnswers(savedProgress.answers);
      toast({
        title: "Progress restored",
        description: "Your previous assessment progress has been restored."
      });
    }
    setIsInitializing(false);
  }, [loadLocalProgress]);

  // Pre-seed cross-module answers for conditional logic (e.g. CIRO preamble in A10)
  useEffect(() => {
    if (effectiveModuleId !== 'exit-structure' || !user?.id) return;

    async function seedCrossModuleAnswers() {
      try {
        const { data } = await supabase
          .from('assessment_progress')
          .select('answers')
          .eq('user_id', user!.id)
          .eq('module_id', 'owner-readiness')
          .maybeSingle();

        if (data?.answers) {
          const a1Answers = data.answers as Record<string, unknown>;
          const seededKeys = ['regulatory_framework', 'cross_border_complexity'] as const;
          const toSeed: Record<string, unknown> = {};
          for (const key of seededKeys) {
            if (a1Answers[key] !== undefined) {
              toSeed[key] = a1Answers[key];
            }
          }
          if (Object.keys(toSeed).length > 0) {
            setAnswers(prev => {
              const updates: Record<string, any> = {};
              for (const [key, value] of Object.entries(toSeed)) {
                if (prev[key] === undefined) {
                  updates[key] = value;
                }
              }
              if (Object.keys(updates).length === 0) return prev;
              return { ...prev, ...updates };
            });
          }
        }
      } catch (err) {
        logger.warn('Failed to seed cross-module answers for A10:', err);
      }
    }

    seedCrossModuleAnswers();
  }, [effectiveModuleId, user?.id]);

  // Pre-seed cross-module answers for A4 (business-valuation) from A3 (business-profile)
  useEffect(() => {
    if (effectiveModuleId !== 'business-valuation' || !user?.id) return;

    async function seedA3Answers() {
      try {
        const { data } = await supabase
          .from('assessment_progress')
          .select('answers')
          .eq('user_id', user!.id)
          .eq('module_id', 'business-profile')
          .maybeSingle();

        if (data?.answers) {
          const a3Answers = data.answers as Record<string, unknown>;
          const seededKeys = ['top_20pct_client_concentration'] as const;
          const toSeed: Record<string, unknown> = {};
          for (const key of seededKeys) {
            if (a3Answers[key] !== undefined) {
              toSeed[key] = a3Answers[key];
            }
          }
          if (Object.keys(toSeed).length > 0) {
            setAnswers(prev => {
              const updates: Record<string, any> = {};
              for (const [key, value] of Object.entries(toSeed)) {
                if (prev[key] === undefined) {
                  updates[key] = value;
                }
              }
              if (Object.keys(updates).length === 0) return prev;
              return { ...prev, ...updates };
            });
          }
        }
      } catch (err) {
        logger.warn('Failed to seed cross-module answers for A4:', err);
      }
    }

    seedA3Answers();
  }, [effectiveModuleId, user?.id]);

  const visibleQuestionsRaw = getVisibleQuestions();
  const visibleQuestions = useMemo(
    () => visibleQuestionsRaw.filter((q) => !/_credentials$/.test(q.id)),
    [visibleQuestionsRaw]
  );

  // Build screen list
  const screenList = useMemo(() => {
    const screens: Array<{ key: string; questions: typeof visibleQuestions; advisorType?: string }> = [];
    const seenGroups = new Set<string>();
    const seenAdvisorTypes = new Set<string>();

    for (const q of visibleQuestions) {
      const advisorType = getAdvisorTypeFromKey(q.id);
      if (advisorType) {
        if (!seenAdvisorTypes.has(advisorType)) {
          seenAdvisorTypes.add(advisorType);
          const advisorQuestions = visibleQuestions.filter(
            (vq) => getAdvisorTypeFromKey(vq.id) === advisorType
          );
          screens.push({ key: `advisor:${advisorType}`, questions: advisorQuestions, advisorType });
        }
        continue;
      }

      const group = (q as any)._questionGroup as string | null | undefined;
      if (group) {
        if (!seenGroups.has(group)) {
          seenGroups.add(group);
          const groupQuestions = visibleQuestions.filter(
            (vq) => (vq as any)._questionGroup === group
          );
          screens.push({ key: `group:${group}`, questions: groupQuestions });
        }
      } else {
        screens.push({ key: q.id, questions: [q] });
      }
    }
    return screens;
  }, [visibleQuestions]);

  const currentScreen = screenList[currentQuestionIndex];
  const currentQuestion = currentScreen?.questions[0];

  logger.debug('ConversationalAssessment state', {
    currentQuestionIndex,
    screensLength: screenList.length,
    currentQuestionId: currentQuestion?.id,
    currentQuestionType: currentQuestion?.type,
    moduleId: effectiveModuleId
  });

  const totalScreens = screenList.length;
  const excludedScreens = screenList.filter(s => {
    const t = s.questions[0]?.type;
    return t === 'welcome' || t === 'completion' || t === 'level-set' || t === 'header' || t === 'recall-bridge';
  }).length;
  const contentScreens = totalScreens - excludedScreens;

  const excludedScreensBeforeCurrent = screenList.slice(0, currentQuestionIndex).filter(s => {
    const t = s.questions[0]?.type;
    return t === 'welcome' || t === 'level-set' || t === 'header' || t === 'recall-bridge';
  }).length;
  const contentScreenIndex = currentQuestionIndex - excludedScreensBeforeCurrent;
  const progressPercentage = contentScreens > 0 ? (contentScreenIndex / contentScreens) * 100 : 0;
  const isLastContentQuestion = currentQuestionIndex === totalScreens - 2;

  const handleNext = async () => {
    const screenQuestions = currentScreen?.questions ?? [currentQuestion];
    const unansweredRequired = screenQuestions.find(
      (q: any) => q.required && !answers[q.id] && q.type !== 'welcome' && q.type !== 'completion'
    );
    if (unansweredRequired) {
      toast({
        title: "Answer required",
        description: "Please answer all required questions before continuing",
        variant: "destructive"
      });
      return;
    }

    if (isLastContentQuestion) {
      if (isFinishing) return;
      setIsFinishing(true);
      try {
        await handleFinish();
      } finally {
        setIsFinishing(false);
      }
    } else {
      await saveToDatabase(currentQuestionIndex, answers);
    }

    setAnimationDirection('right');
    if (currentQuestionIndex < totalScreens - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      // Scroll the overlay content area back to top
      const scrollContainer = document.querySelector('.overflow-y-auto');
      if (scrollContainer) scrollContainer.scrollTop = 0;
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    setAnimationDirection('left');
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      const scrollContainer = document.querySelector('.overflow-y-auto');
      if (scrollContainer) scrollContainer.scrollTop = 0;
      window.scrollTo(0, 0);
    }
  };

  const handleSave = async () => {
    await manualSave(currentQuestionIndex, answers);
  };

  const handleFinish = async () => {
    const userId = user?.id;
    const moduleIdForDb = effectiveModuleId || 'owner-readiness';
    
    console.log('[Assessment] handleFinish called. userId:', userId, 'moduleId:', moduleIdForDb, 'assessmentId:', assessmentId);

    await saveToDatabase(currentQuestionIndex, answers);
    await saveAllResponses(answers);
    clearProgress();
    
    if (userId) {
      const { error: moduleError } = await supabase
        .from('assessment_modules')
        .upsert({
          user_id: userId,
          module_id: moduleIdForDb,
          status: 'complete',
          completion_date: new Date().toISOString(),
        } as never, {
          onConflict: 'user_id,module_id'
        });

      if (moduleError) {
        console.error('[Assessment] Failed to update assessment_modules:', moduleError);
      } else {
        console.log('[Assessment] Module marked complete:', moduleIdForDb);
      }
    }
    
    if (userId && assessmentId) {
      try {
        const { overallScore, success } = await calculateScores(userId, teamId, assessmentId);
        
        if (success) {
          console.log(`[Scoring] mySuccession Score: ${overallScore}%`);
          // Merge with existing metadata instead of overwriting
          const { data: existingModule } = await supabase
            .from('assessment_modules')
            .select('metadata')
            .eq('user_id', userId)
            .eq('module_id', moduleIdForDb)
            .maybeSingle();
          const existingMeta = (existingModule?.metadata as Record<string, unknown>) || {};
          await supabase
            .from('assessment_modules')
            .update({ 
              final_score: overallScore,
              metadata: { ...existingMeta, scoring_success: true, scored_at: new Date().toISOString() }
            } as never)
            .eq('user_id', userId)
            .eq('module_id', moduleIdForDb);
        } else {
          console.warn('[Scoring] Calculation completed but returned success=false');
          const { data: existingModule2 } = await supabase
            .from('assessment_modules')
            .select('metadata')
            .eq('user_id', userId)
            .eq('module_id', moduleIdForDb)
            .maybeSingle();
          const existingMeta2 = (existingModule2?.metadata as Record<string, unknown>) || {};
          await supabase
            .from('assessment_modules')
            .update({ 
              metadata: { ...existingMeta2, scoring_success: false, scored_at: new Date().toISOString() }
            } as never)
            .eq('user_id', userId)
            .eq('module_id', moduleIdForDb);
        }
      } catch (error) {
        console.error('[Assessment] Scoring failed:', error);
        const { data: existingModule3 } = await supabase
          .from('assessment_modules')
          .select('metadata')
          .eq('user_id', userId)
          .eq('module_id', moduleIdForDb)
          .maybeSingle();
        const existingMeta3 = (existingModule3?.metadata as Record<string, unknown>) || {};
        await supabase
          .from('assessment_modules')
          .update({ 
            metadata: { ...existingMeta3, scoring_success: false, error: String(error) }
          } as never)
          .eq('user_id', userId)
          .eq('module_id', moduleIdForDb);
      }
    }

    if (userId && moduleIdForDb === 'advisory-team' && teamId) {
      try {
        await syncAdvisorsToTeamMembers(userId, teamId);
      } catch (err) {
        console.error('[Assessment] A6 advisor sync failed:', err);
      }
    }

    if (userId && moduleIdForDb === 'business-profile') {
      try {
        await supabase.rpc('sync_a3_to_teams', {
          p_user_id: userId,
          p_answers: answers as any
        });
        console.log('[Assessment] A3 sync_a3_to_teams completed');
      } catch (err) {
        console.error('[Assessment] A3 sync_a3_to_teams failed:', err);
      }
    }

    if (userId && moduleIdForDb === 'successor-strategy' && teamId) {
      try {
        await syncA8SuccessorsToCandidates(userId, teamId);
      } catch (err) {
        console.error('[Assessment] A8 successor sync failed:', err);
      }
    }

    toast({
      title: "Assessment completed!",
      description: "Your assessment has been completed and saved."
    });
  };

  const handleAnswerChange = (questionId: string, value: QuestionAnswer) => {
    const newAnswers = {
      ...answers,
      [questionId]: value
    };
    
    const updatedVisible = moduleQuestions.filter(q => {
      if (!q.conditionalDisplay) return true;
      return evaluateConditionalDisplay(q.conditionalDisplay, newAnswers);
    });
    const visibleIds = new Set(updatedVisible.map(q => q.id));
    for (const q of moduleQuestions) {
      if (q.conditionalDisplay && !visibleIds.has(q.id)) {
        if ('skipDefaultValue' in q && q.skipDefaultValue !== undefined) {
          newAnswers[q.id] = q.skipDefaultValue;
        } else {
          delete newAnswers[q.id];
        }
      }
    }
    
    setAnswers(newAnswers);
    autoSave(currentQuestionIndex, newAnswers);
  };

  // Handle recall bridge completion — store target_exit_date and advance
  const handleRecallBridgeContinue = useCallback(async (targetDate: string | null) => {
    if (targetDate) {
      const newAnswers = { ...answers, target_exit_date: targetDate };
      setAnswers(newAnswers);
      await saveToDatabase(currentQuestionIndex, newAnswers);
    }
    setAnimationDirection('right');
    setCurrentQuestionIndex(prev => prev + 1);
    const scrollContainer = document.querySelector('.overflow-y-auto');
    if (scrollContainer) scrollContainer.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [answers, currentQuestionIndex, saveToDatabase]);

  // Skip recall bridge on re-entry if target_exit_date already stored
  const currentScreenType = screenList[currentQuestionIndex]?.questions[0]?.type;
  useEffect(() => {
    if (currentScreenType === 'recall-bridge' && answers.target_exit_date) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  }, [currentScreenType, answers.target_exit_date]);

  if (isInitializing || dbLoading || !assessmentMeta) {
    return <div className="h-full flex items-center justify-center">
        <InlineLoading size="lg" text="Loading assessment..." />
      </div>;
  }

  // Show placeholder for assessments with zero questions
  if (!dbLoading && !dbError && dbQuestions.length === 0) {
    return <AssessmentPlaceholder moduleId={effectiveModuleId} onExit={onExit} />;
  }

  // Show error state
  if (dbError) {
    return <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <h3 className="text-lg font-semibold mb-2">Failed to load assessment</h3>
          <p className="text-muted-foreground mb-4">{dbError}</p>
          <Button onClick={onExit} variant="outline">Go Back</Button>
        </div>
      </div>;
  }

  // Show intro screen for ALL assessments before the first question
  if (showIntro && assessmentMeta) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4">
          <AssessmentIntroScreen
            meta={assessmentMeta}
            moduleId={effectiveModuleId || undefined}
            onBegin={() => setShowIntro(false)}
          />
        </div>
      </div>
    );
  }

  // Handle missing question gracefully
  if (!currentQuestion) {
    Sentry.captureException(new Error('Current question is undefined'), {
      tags: {
        context: 'assessment_error',
        component: 'ConversationalAssessment'
      },
      extra: {
        message: 'Current question is undefined',
        currentQuestionIndex,
        visibleQuestionsLength: visibleQuestions?.length,
        moduleId: effectiveModuleId,
        answers
      }
    });
    return <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <h3 className="text-lg font-semibold mb-2">Question not found</h3>
          <p className="text-muted-foreground mb-4">
            Unable to load question {currentQuestionIndex + 1} of {visibleQuestions?.length || 0} for module: {effectiveModuleId}
          </p>
          <div className="space-x-2">
            <Button onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))} variant="outline">
              Go Back
            </Button>
            <Button onClick={() => setCurrentQuestionIndex(0)} variant="default">
              Restart Assessment
            </Button>
          </div>
        </div>
      </div>;
  }

  const isWelcomeScreen = currentQuestion.type === 'welcome';
  const isCompletionScreen = currentQuestion.type === 'completion';
  const isLevelSetScreen = currentQuestion.type === 'level-set';
  const isRecallBridgeScreen = currentQuestion.type === 'recall-bridge';
  const isContentScreen = !isWelcomeScreen && !isCompletionScreen && !isLevelSetScreen && !isRecallBridgeScreen;

  const handleRetry = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
  };

  return (
    <AssessmentErrorBoundary onRetry={handleRetry}>
      <div className="h-full flex flex-col max-w-full">
        {/* Progress bar - only show for content screens */}
        {isContentScreen && (
          <div className="px-6 pt-4">
            <div className="flex justify-between items-center text-[13px] text-muted-foreground mb-2">
              <span>Question {contentScreenIndex + 1} of {contentScreens}</span>
              {isSaving && <InlineLoading size="sm" text="Saving..." />}
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>
        )}
        
        {/* Main content area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className={`flex-1 flex flex-col overflow-y-auto px-6 py-6 bg-background`}>
            <SlideAnimation direction={animationDirection} className="flex w-full flex-1 items-start justify-center">
              {isContentScreen ? (
                <div className="w-full max-w-3xl mx-auto">
                  <div className="p-6">
                    {currentScreen && (currentScreen as any).advisorType
                      ? <AdvisorTypeSectionCard
                          advisorType={(currentScreen as any).advisorType}
                          questions={currentScreen.questions as any}
                          answers={answers}
                          onAnswerChange={handleAnswerChange}
                          onNext={handleNext}
                          onFinish={onComplete}
                        />
                      : currentScreen && currentScreen.questions.length > 1
                        ? <CompositeQuestionGroup
                            questions={currentScreen.questions as any}
                            answers={answers}
                            onAnswerChange={handleAnswerChange}
                            onNext={handleNext}
                            onFinish={onComplete}
                          />
                        : <QuestionRenderer question={currentQuestion} answers={answers} onAnswerChange={handleAnswerChange} onNext={handleNext} onFinish={onComplete} onNextAssessment={onNextAssessment} />
                    }
                  </div>
                </div>
              ) : isRecallBridgeScreen ? (
                <RecallBridgeScreen onContinue={handleRecallBridgeContinue} />
              ) : (
                // Welcome, level-set, completion screens render without the card wrapper
                currentScreen && (currentScreen as any).advisorType
                  ? <AdvisorTypeSectionCard
                      advisorType={(currentScreen as any).advisorType}
                      questions={currentScreen.questions as any}
                      answers={answers}
                      onAnswerChange={handleAnswerChange}
                      onNext={handleNext}
                      onFinish={onComplete}
                    />
                  : currentScreen && currentScreen.questions.length > 1
                    ? <CompositeQuestionGroup
                        questions={currentScreen.questions as any}
                        answers={answers}
                        onAnswerChange={handleAnswerChange}
                        onNext={handleNext}
                        onFinish={onComplete}
                      />
                    : <QuestionRenderer question={currentQuestion} answers={answers} onAnswerChange={handleAnswerChange} onNext={handleNext} onFinish={onComplete} onNextAssessment={onNextAssessment} />
              )}
            </SlideAnimation>
          </div>
        </div>
        
        {/* Navigation footer - only show for content screens */}
        {isContentScreen && (
          <div className="border-t border-border px-6 py-4 bg-background flex-shrink-0">
            <AssessmentNavigation
              onPrevious={handlePrevious}
              onNext={handleNext}
              onSave={handleSave}
              isPreviousDisabled={currentQuestionIndex === 0}
              isNextDisabled={
                // Allocation input must sum to 100%
                (currentQuestion?.type === 'allocation_input' && (() => {
                  const val = answers[currentQuestion.id];
                  if (!val || typeof val !== 'object' || Array.isArray(val)) return true;
                  const total = Object.values(val as Record<string, number>).reduce((s: number, v: number) => s + (v || 0), 0);
                  return Math.abs(total - 100) >= 0.01;
                })()) ||
                // "Other" free-text requires 3+ characters
                (currentQuestion && (currentQuestion.type === 'radio' || currentQuestion.type === 'checkbox') && (() => {
                  const val = answers[currentQuestion.id];
                  const hasOtherSelected = typeof val === 'string'
                    ? isOtherValue(val)
                    : Array.isArray(val) && val.some(v => isOtherValue(String(v)));
                  if (!hasOtherSelected) return false;
                  const otherText = answers[otherTextKey(currentQuestion.id)];
                  return typeof otherText !== 'string' || otherText.trim().length < 3;
                })())
              }
              isLastQuestion={currentQuestionIndex === totalScreens - 2}
              isSaving={isSaving}
              isFinishing={isFinishing}
            />
          </div>
        )}
      </div>
    </AssessmentErrorBoundary>
  );
}
